import { ref, reactive, computed, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import {
  GetDeliveryAreaDocument,
  type GetDeliveryAreaQuery,
  ListDeliveryAreaDocument,
  type ListDeliveryAreaQuery,
  type ListDeliveryAreaQueryVariables,
  ListDeliveryAvailabilityDocument,
  type ListDeliveryAvailabilityQuery,
  type TransPickupRequestCreateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type { DeliveryAvailabilityData, DeliveryArea, DeliveryStartMinitOption } from "../types";
import { WEEKS } from "../constants";

/** 配達時間帯管理に必要な依存をまとめた型。 */
interface UseDeliveryMoveRegisterDeliveryTimeParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  transPickupRequestCreateInput: TransPickupRequestCreateInput;
  wordList: Ref<Record<string, string>>;
}

/** 配達可能時間帯管理、都度便の時刻ロジックを扱う composable。 */
export const useDeliveryMoveRegisterDeliveryTime = (params: UseDeliveryMoveRegisterDeliveryTimeParams) => {
  const {
    errdialog,
    errMessage,
    transPickupRequestCreateInput,
    wordList,
  } = params;

  // 配達エリア名
  const listDeliveryArea = ref<DeliveryArea[] | null>(null);
  const deliveryAreaName = ref('');
  const acceptableEndTime = ref('');

  // 配達開始時間の選択ボックス
  const deliveryStartHourItem = reactive<number[]>([]);
  const deliveryStartHourValues = ref<number | null | undefined>(undefined);

  // 配達開始時間の選択ボックス
  const deliveryStartMinitItem = reactive<DeliveryStartMinitOption[]>([
    { label: '00', deliveryStartMinit: 0 },
    { label: '30', deliveryStartMinit: 1 }
  ]);
  const deliveryStartMinitValues = ref<DeliveryStartMinitOption | null | undefined>(undefined);

  // 配達希望時間
  const irregularDeliveryStartTime = ref('');
  const irregularDeliveryEndTime = ref('');
  const deliveryId_0_Flg = ref(false);
  const deliveryStartTime = ref('');
  const deliveryEndTime = ref('');
  const overDeliveryEndTime = ref(false);

  // ２件分配達
  const doubleDeliOriconCount = ref<number | null | undefined>(null);
  const isDoubleDeli = ref(false);
  // チャーター便
  const charterDeliOriconCount = ref<number | null | undefined>(null);
  const isCharterDeli = ref(false);

  // 配達便情報のヘッダー
  const deliveryAvailabilityHeader = ref<Map<string, string>>(new Map());
  const deliveryAvailabilityData = reactive<DeliveryAvailabilityData[][]>([]);
  const disp_deliveryAvailabilityData = reactive<DeliveryAvailabilityData[][]>([]);
  const display_delivery_time = ref('');

  // 便選択色変更
  const deliverySelectDayBox = ref<string | number>('');
  const deliverySelectBox = ref<string | number>('');

  // ２件分配達メッセージ取得＆置換
  const doubleDeliCheckLabel = computed(() =>
    (wordList.value.lbl_double_entry_chk || '').replace(
      '{{n}}',
      (doubleDeliOriconCount.value ?? 0).toString()
    )
  );

  // チャーター便メッセージ取得＆置換
  const charterDeliChkLabel = computed(() =>
    (wordList.value.lbl_charter_chk || '').replace(
      '{{n}}',
      (charterDeliOriconCount.value ?? 0).toString()
    )
  );

  // エリアボタン選択時 @click
  async function onClickListDeliveryArea(item: DeliveryArea | null | undefined): Promise<void> {
    if (!item) return;
    deliveryAvailabilityHeader.value = new Map();
    disp_deliveryAvailabilityData.length = 0;
    let addAry: DeliveryAvailabilityData[] = [];

    transPickupRequestCreateInput.area_code = item.area_code;
    deliveryAreaName.value = item.area_name;
    acceptableEndTime.value = item.acceptable_end_time;

    deliveryAvailabilityData.forEach(listVal => {
      addAry = [];
      listVal.forEach(value => {
        if (String(value.area_code) === String(item.area_code)) {
          addAry.push(value);
          if (value.delivery_id_desc !== '') {
            deliveryAvailabilityHeader.value.set(value.delivery_id_desc, value.delivery_id_desc);
          }
          if (listVal[0].delivery_id === 0) {
            deliveryId_0_Flg.value = true;
            deliveryStartTime.value = listVal[0].delivery_start_time;
          } else {
            deliveryId_0_Flg.value = false;
            deliveryStartTime.value = '';
          }
        }
      });
      if (addAry.length > 0) {
        disp_deliveryAvailabilityData.push(addAry);
      }
    });
  }

  // エリア一覧情報を取得する
  async function getAreaList(address: string): Promise<void> {
    if (address === '' || transPickupRequestCreateInput.ship_from_shop_code === '') {
      return;
    }

    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.length = 0;
    disp_deliveryAvailabilityData.length = 0;
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.splice(0);
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    listDeliveryArea.value = null;
    transPickupRequestCreateInput.area_code = '';

    const apolloClient = await generateApolloClient();
    try {
      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: transPickupRequestCreateInput.ship_from_shop_code,
          address: address
        }
      });
      // GraphQLクエリ結果はread-onlyのため、配列をディープコピー
      listDeliveryArea.value = listArea.data.listDeliveryArea?.map(item => ({ ...item })) ?? null;

      await getAreaInfo();

      if (listDeliveryArea.value !== null && listDeliveryArea.value.length === 1) {
        if (listDeliveryArea.value.length > 0) {
          transPickupRequestCreateInput.area_code = listDeliveryArea.value[0]!.area_code;
          await onClickListDeliveryArea(listDeliveryArea.value[0]);
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = '配達エリア一覧の取得に失敗しました。';
        throw err;
      } else {
        throw new Error('配達エリア一覧の取得に失敗しました。（不明なエラー）');
      }
    }
  }

  // エリア情報取得
  async function getAreaInfo(): Promise<void> {
    const apolloClient = await generateApolloClient();
    let acceptableEndTimeAry: string[] = [];

    try {
      for (let i = 0; i < (listDeliveryArea.value ? listDeliveryArea.value.length : 0); i++) {
        const area_code = listDeliveryArea.value![i]!.area_code;
        const area_data: ApolloQueryResult<GetDeliveryAreaQuery> = await apolloClient.query<GetDeliveryAreaQuery>({
          query: GetDeliveryAreaDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: transPickupRequestCreateInput.ship_from_shop_code,
            area_code: area_code
          }
        });
        const deliveryArea = area_data.data.getDeliveryArea;
        if (!deliveryArea || !deliveryArea.acceptable_end_time) {
          continue;
        }
        acceptableEndTimeAry = deliveryArea.acceptable_end_time.split(':');
        if (acceptableEndTimeAry.length < 2) {
          continue;
        }
        const formattedTime = acceptableEndTimeAry[0].padStart(2, '0') + ':' + acceptableEndTimeAry[1].padStart(2, '0');
        // GraphQLクエリ結果はread-onlyのため、新しいオブジェクトを作成して代入
        listDeliveryArea.value![i] = {
          ...listDeliveryArea.value![i]!,
          acceptable_end_time: formattedTime
        };
        if (listDeliveryArea.value![i]!.area_code === transPickupRequestCreateInput.area_code) {
          deliveryAreaName.value = deliveryArea.area_name;
          acceptableEndTime.value = formattedTime;
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = '配達エリアの取得に失敗しました。';
        throw err;
      } else {
        throw new Error('配達エリアの取得に失敗しました。（不明なエラー）');
      }
    }

    await getDeliveryTimeList();
  }

  // 配達時間一覧取得する
  async function getDeliveryTimeList(): Promise<void> {
    try {
      const apolloClient = await generateApolloClient();

      for (let i = 0; i < (listDeliveryArea.value ? listDeliveryArea.value.length : 0); i++) {
        const area_code = listDeliveryArea.value![i]!.area_code;
        const result: ApolloQueryResult<ListDeliveryAvailabilityQuery> = await apolloClient.query<ListDeliveryAvailabilityQuery>({
          query: ListDeliveryAvailabilityDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: transPickupRequestCreateInput.ship_from_shop_code,
            area_code: area_code
          },
          fetchPolicy: 'no-cache'
        });

        let list = result.data.listDeliveryAvailability ?? [];
        if (!list || list.length <= 0) {
          errdialog.value = true;
          errMessage.value = '選択した住所で配達可能な時間帯はありません。';
          transPickupRequestCreateInput.area_code = '';
          deliveryAreaName.value = '';
          deliveryAvailabilityHeader.value = new Map();
          deliveryAvailabilityData.length = 0;
          disp_deliveryAvailabilityData.length = 0;
          return;
        }

        let defaultAreaCode = '';
        let currentDate = '';
        let currentAvailability: DeliveryAvailabilityData[] = [];
        let idx = 0;
        let addDeliveryIdAry: number[] = [];
        let addCnt = 0;
        let current_area_code = '';

        addDeliveryIdAry = list.map(item => item!.delivery_id);
        addDeliveryIdAry = Array.from(new Set(addDeliveryIdAry));

        list.forEach(value => {
          if (value) {
            if (defaultAreaCode.length === 0) {
              defaultAreaCode = value.area_code;
            }
            if (value.area_code !== defaultAreaCode) {
              return;
            }
            current_area_code = listDeliveryArea.value![i]!.area_code;
            value.delivery_date = value.delivery_date.substring(0, 10);
            if (currentDate.length === 0 || currentDate !== value.delivery_date) {
              currentDate = value.delivery_date;
              if (currentAvailability.length > 0) {
                if (addCnt < addDeliveryIdAry.length) {
                  for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
                    currentAvailability.push({
                      key: ++idx,
                      area_code: current_area_code,
                      delivery_id: addDeliveryIdAry[j],
                      delivery_id_desc: '',
                      delivery_date: value.delivery_date,
                      delivery_start_time: '',
                      delivery_end_time: '',
                      availability: '×'
                    });
                  }
                }
                deliveryAvailabilityData.push(currentAvailability);
                if (current_area_code === transPickupRequestCreateInput.area_code) {
                  disp_deliveryAvailabilityData.push(currentAvailability);
                }
              }
              currentAvailability = [];
              idx = 0;
              addCnt = 0;
            }

            for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
              if (addDeliveryIdAry[j] !== value.delivery_id) {
                currentAvailability.push({
                  key: ++idx,
                  area_code: current_area_code,
                  delivery_id: addDeliveryIdAry[j],
                  delivery_id_desc: '',
                  delivery_date: value.delivery_date,
                  delivery_start_time: '',
                  delivery_end_time: '',
                  availability: '×'
                });
              } else {
                addCnt = j;
                break;
              }
            }

            currentAvailability.push({
              key: ++idx,
              area_code: current_area_code,
              delivery_id: value.delivery_id,
              delivery_id_desc: value.delivery_id_desc,
              delivery_date: value.delivery_date,
              delivery_start_time: value.delivery_start_time,
              delivery_end_time: value.delivery_end_time,
              availability: value.availability.trim()
            });
            addCnt++;
          }
        });

        if (currentAvailability.length > 0) {
          if (addCnt < addDeliveryIdAry.length) {
            for (let j = addCnt; j < addDeliveryIdAry.length; j++) {
              currentAvailability.push({
                key: ++idx,
                area_code: current_area_code,
                delivery_id: addDeliveryIdAry[j],
                delivery_id_desc: '',
                delivery_date: currentDate,
                delivery_start_time: '',
                delivery_end_time: '',
                availability: '×'
              });
            }
          }
          deliveryAvailabilityData.push(currentAvailability);
          if (current_area_code === transPickupRequestCreateInput.area_code) {
            disp_deliveryAvailabilityData.push(currentAvailability);
          }
        }
        list = [];
      }

      if (listDeliveryArea.value && listDeliveryArea.value.length === 1) {
        if (deliveryAvailabilityData[0][0].delivery_id === 0) {
          deliveryId_0_Flg.value = true;
          deliveryStartTime.value = deliveryAvailabilityData[0][0].delivery_start_time;
        }
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : '選択した住所は配達エリア外です。';

      transPickupRequestCreateInput.area_code = '';
      deliveryAreaName.value = '';
      deliveryAvailabilityHeader.value = new Map();
      deliveryAvailabilityData.length = 0;

      return;
    }
  }

  // 便選択 @click
  async function onDeliveryAvailabilityClick(
    date: string,
    deliveryid: string,
    delivery_start_time: string,
    delivery_end_time: string,
    deliverySelectDay: string,
    deliverySelect: string
  ): Promise<void> {
    transPickupRequestCreateInput.delivery_id = Number(deliveryid);
    transPickupRequestCreateInput.required_delivery_date = date;

    let strDeliveryId = '';
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.splice(0);
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;

    if (transPickupRequestCreateInput.delivery_id === 0) {
      let startHour = 0;
      const now = new Date();
      const nowDate =
        now.getFullYear() +
        '-' +
        String(now.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(now.getDate()).padStart(2, '0');
      const deliveryStartTimeAry = delivery_start_time.split(':');

      if (
        date !== nowDate ||
        (date === nowDate &&
          (Number(deliveryStartTimeAry[0]) > now.getHours() ||
            (Number(deliveryStartTimeAry[0]) === now.getHours() &&
              Number(deliveryStartTimeAry[1]) > now.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else {
        if (now.getMinutes() < 30) {
          startHour = now.getHours();
        } else {
          startHour = now.getHours() + 1;
        }
      }

      const endTimeAry = acceptableEndTime.value.split(':');
      for (let i = startHour; i <= Number(endTimeAry[0]); i++) {
        deliveryStartHourItem.push(i);
      }

      deliveryStartMinitItem.splice(0);
      deliveryStartMinitItem.push(
        { label: '00', deliveryStartMinit: 0 },
        { label: '30', deliveryStartMinit: 1 }
      );

      strDeliveryId = '都度便';
      deliveryId_0_Flg.value = true;
      deliveryStartTime.value = delivery_start_time;
      deliveryEndTime.value = delivery_end_time;

      await setDeliveryStartTime();
      await setDeliveryEndTime();

      transPickupRequestCreateInput.irregular_delivery_start_time = null;
      transPickupRequestCreateInput.irregular_delivery_end_time = null;
    } else {
      strDeliveryId = transPickupRequestCreateInput.delivery_id + '便';
    }

    display_delivery_time.value =
      transPickupRequestCreateInput.required_delivery_date + '　' + strDeliveryId;
    deliverySelectDayBox.value = deliverySelectDay;
    deliverySelectBox.value = deliverySelect;
  }

  // 配達希望時間（時）変更イベント @change
  async function onChangeDeliveryStartHour(): Promise<void> {
    if (deliveryStartHourValues.value !== undefined) {
      if (!await chkDeliveryStartTime_Now()) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、現在以降の時間を入力してください。';
        return;
      }
      if (!await chkDeliveryStartTime()) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。';
        return;
      }
      if (!await chkDeliveryEndTimeOver()) {
        deliveryStartHourValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、受付終了時間以前の時間を入力してください。';
        return;
      }

      const now = new Date();
      const deliveryStartTimeAry = deliveryStartTime.value.split(':');
      const endTimeAry = acceptableEndTime.value.split(':');
      deliveryStartMinitItem.splice(0);
      deliveryStartMinitItem.push(
        { label: '00', deliveryStartMinit: 0 },
        { label: '30', deliveryStartMinit: 1 }
      );

      if (deliveryStartHourValues.value !== undefined && deliveryStartHourValues.value !== null) {
        if (
          (deliveryStartHourValues.value === now.getHours() && now.getMinutes() < 30) ||
          (deliveryStartHourValues.value === Number(deliveryStartTimeAry[0]) && deliveryStartTimeAry[1] === '30')
        ) {
          deliveryStartMinitItem.shift();
          deliveryStartMinitValues.value = { label: '30', deliveryStartMinit: 1 };
        } else if (
          deliveryStartHourValues.value === Number(endTimeAry[0]) &&
          endTimeAry[1] === '00'
        ) {
          deliveryStartMinitItem.pop();
          deliveryStartMinitValues.value = { label: '00', deliveryStartMinit: 0 };
        }
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  }

  // 配達希望時間（分）変更イベント @change
  async function onChangeDeliveryStartMinit(): Promise<void> {
    if (deliveryStartMinitValues.value !== undefined) {
      if (!await chkDeliveryStartTime_Now()) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、現在以降の時間を入力してください。';
        return;
      }
      if (!await chkDeliveryStartTime()) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、選択した便の配達開始時間以降の時間を入力してください。';
        return;
      }
      if (!await chkDeliveryEndTimeOver()) {
        deliveryStartMinitValues.value = undefined;
        errdialog.value = true;
        errMessage.value = '配達希望時間は、受付終了時間以前の時間を入力してください。';
        return;
      }
    }

    await setDeliveryStartTime();
    await setDeliveryEndTime();
  }

  // 配達希望時間（過去日時チェック） @change
  async function chkDeliveryStartTime_Now(): Promise<boolean> {
    const now = new Date();
    const nowDate =
      now.getFullYear() +
      '-' +
      String(now.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(now.getDate()).padStart(2, '0');

    if (
      transPickupRequestCreateInput.required_delivery_date === nowDate &&
      deliveryStartHourValues.value !== null &&
      deliveryStartHourValues.value !== undefined &&
      (deliveryStartHourValues.value < now.getHours() ||
        (deliveryStartHourValues.value === now.getHours() &&
          deliveryStartMinitValues.value !== null &&
          deliveryStartMinitValues.value !== undefined &&
          Number(deliveryStartMinitValues.value.label) < now.getMinutes()))
    ) {
      // 選択時間が現在時刻以前 → 無効
      return false;
    } else {
      // 受付終了時間 + 3時間
      const endTime = (deliveryStartHourValues.value as number) + 3;
      // 配達終了時間を「hh:mm」形式で分割
      const deliveryEndTimeAry = deliveryEndTime.value.split(':');
      const minuteLabel = deliveryStartMinitValues.value?.label ?? '00';
      if (
        // 受付終了時間 + 3時間が配達終了時間を超過している場合、配達終了時間をセット
        endTime > Number(deliveryEndTimeAry[0]) ||
        (endTime === Number(deliveryEndTimeAry[0]) &&
          minuteLabel !== deliveryEndTimeAry[1])
      ) {
        // 配達終了時間をセット
        irregularDeliveryEndTime.value = deliveryEndTime.value;
      } else {
        // 受付終了時間 + 3時間をセット
        irregularDeliveryEndTime.value =
          String(endTime).padStart(2, '0') + ':' + minuteLabel + ':00';
      }
      return true;
    }
  }

  // 配達希望時間（配達終了時刻超過チェック）
  async function chkDeliveryEndTimeOver(): Promise<boolean> {
    const deliveryEndTimeAry = deliveryEndTime.value.split(':');
    if (
      (deliveryStartHourValues.value as number) > Number(deliveryEndTimeAry[0]) ||
      (deliveryStartHourValues.value === Number(deliveryEndTimeAry[0]) &&
        Number(deliveryStartMinitValues.value!.label) > Number(deliveryEndTimeAry[1]))
    ) {
      return false;
    } else {
      return true;
    }
  }

  // 配達希望時間変更イベント @change
  async function chkDeliveryStartTime(): Promise<boolean> {
    const deliveryStartTimeAry = deliveryStartTime.value.split(':');
    if (
      (deliveryStartHourValues.value !== null &&
        deliveryStartHourValues.value !== undefined &&
        deliveryStartHourValues.value < Number(deliveryStartTimeAry[0])) ||
      (deliveryStartHourValues.value === Number(deliveryStartTimeAry[0]) &&
        deliveryStartMinitValues.value !== null &&
        deliveryStartMinitValues.value !== undefined &&
        Number(deliveryStartMinitValues.value.label) < Number(deliveryStartTimeAry[1]))
    ) {
      return false;
    } else {
      return true;
    }
  }

  // 配達開始時間セット
  async function setDeliveryStartTime(): Promise<void> {
    const now = new Date();
    const deliveryStartTimeAry = deliveryStartTime.value.split(':');
    if (
      deliveryStartHourValues.value !== undefined &&
      deliveryStartHourValues.value !== null &&
      deliveryStartMinitValues.value !== undefined &&
      deliveryStartMinitValues.value !== null
    ) {
      irregularDeliveryStartTime.value =
        String(deliveryStartHourValues.value).padStart(2, '0') +
        ':' +
        deliveryStartMinitValues.value.label;
    } else {
      let hour = 0;
      let minut = 0;
      if (
        now.getHours() < Number(deliveryStartTimeAry[0]) ||
        (now.getHours() === Number(deliveryStartTimeAry[0]) &&
          now.getMinutes() < Number(deliveryStartTimeAry[1]))
      ) {
        hour = Number(deliveryStartTimeAry[0]);
        minut = Number(deliveryStartTimeAry[1]);
      } else {
        if (now.getMinutes() < 30) {
          hour = now.getHours();
          minut = 30;
        } else {
          hour = now.getHours() + 1;
          minut = 0;
        }
      }
      irregularDeliveryStartTime.value =
        String(hour).padStart(2, '0') + ':' + String(minut).padStart(2, '0');
    }
  }

  // 配達終了時間セット
  async function setDeliveryEndTime(): Promise<void> {
    const deliveryStartTimeAry = irregularDeliveryStartTime.value.split(':');
    const deliveryEndTimeAry = deliveryEndTime.value.split(':');
    const endTime = Number(deliveryStartTimeAry[0]) + 3;
    if (
      endTime > Number(deliveryEndTimeAry[0]) ||
      (endTime === Number(deliveryEndTimeAry[0]) &&
        deliveryStartTimeAry[1] !== deliveryEndTimeAry[1])
    ) {
      irregularDeliveryEndTime.value = deliveryEndTime.value;
    } else {
      irregularDeliveryEndTime.value =
        String(endTime).padStart(2, '0') + ':' + deliveryStartTimeAry[1];
    }
  }

  // 曜日変換
  function formatDate(date: string): string {
    let retDate = '';
    if (date) {
      const _date = new Date(date);
      retDate =
        _date.getMonth() + 1 + '/' + _date.getDate() + '(' + WEEKS[_date.getDay()] + ')';
    }
    return retDate;
  }

  return {
    listDeliveryArea,
    deliveryAreaName,
    acceptableEndTime,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitItem,
    deliveryStartMinitValues,
    irregularDeliveryStartTime,
    irregularDeliveryEndTime,
    deliveryId_0_Flg,
    deliveryStartTime,
    deliveryEndTime,
    overDeliveryEndTime,
    doubleDeliOriconCount,
    isDoubleDeli,
    charterDeliOriconCount,
    isCharterDeli,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    display_delivery_time,
    deliverySelectDayBox,
    deliverySelectBox,
    doubleDeliCheckLabel,
    charterDeliChkLabel,
    onClickListDeliveryArea,
    getAreaList,
    getAreaInfo,
    getDeliveryTimeList,
    onDeliveryAvailabilityClick,
    onChangeDeliveryStartHour,
    onChangeDeliveryStartMinit,
    formatDate,
  };
};
